import React, { useState, useEffect, useRef } from 'react';
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { highlightPlugin, Trigger } from '@react-pdf-viewer/highlight';
import { searchPlugin } from '@react-pdf-viewer/search';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { AlertCircle, Loader, FileText, FileSpreadsheet, File, ChevronLeft, ChevronRight, ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import SmartSpreadsheetViewer from './SmartSpreadsheetViewer';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import { toolbarPlugin } from '@react-pdf-viewer/toolbar';
import { MinimalButton } from '@react-pdf-viewer/core';
import { 
  CurrentPageInput, 
  NumberOfPages 
} from '@react-pdf-viewer/page-navigation';

// Import styles
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import '@react-pdf-viewer/highlight/lib/styles/index.css';
import '@react-pdf-viewer/search/lib/styles/index.css';
import '@react-pdf-viewer/page-navigation/lib/styles/index.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

// Add getFileIcon function
const getFileIcon = (fileType) => {
  switch (true) {
    case fileType.includes('spreadsheetml.sheet'):
    case fileType.includes('ms-excel'):
    case fileType.includes('csv'):
      return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    case fileType.includes('pdf'):
      return <File className="w-5 h-5 text-red-500" />;
    case fileType.includes('text'):
      return <FileText className="w-5 h-5 text-blue-500" />;
    default:
      return <File className="w-5 h-5 text-gray-500" />;
  }
};

const UniversalDocumentViewer = ({ 
  documentUrl, 
  documentType, 
  documentId,
  documents,
  user, 
  highlights, 
  activeHighlight, 
  onAskAboutData,
  onDocumentSwitch
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);
  
  // Create plugin instances
  const pageNavigationPluginInstance = pageNavigationPlugin();
  const searchPluginInstance = searchPlugin();
  const { highlight } = searchPluginInstance;
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const zoomPluginInstance = zoomPlugin();
  const toolbarPluginInstance = toolbarPlugin();
  const { Toolbar } = toolbarPluginInstance;

  // Ref to track the last processed highlight
  const lastProcessedHighlightRef = useRef(null);

  const handleHighlightAndNavigation = async () => {
    if (!activeHighlight || !isDocumentLoaded || !documentType.includes('pdf')) {
      return;
    }

    if (activeHighlight.documentId !== documentId) {
      return;
    }

    // Check if we've already processed this highlight
    if (lastProcessedHighlightRef.current?.page === activeHighlight.page && 
        lastProcessedHighlightRef.current?.text === activeHighlight.text) {
      return;
    }

    console.log('Handling highlight and navigation:', activeHighlight);

    try {
      // First navigate to the page
      if (pageNavigationPluginInstance.jumpToPage) {
        console.log('Navigating to page:', activeHighlight.page);
        await pageNavigationPluginInstance.jumpToPage(activeHighlight.page);
        
        // Add a small delay before highlighting to ensure the page is rendered
        await new Promise(resolve => setTimeout(resolve, 100));

        // Then highlight the text
        if (activeHighlight.text && highlight) {
          console.log('Highlighting text:', activeHighlight.text);
          const newKeyword = {
            keyword: activeHighlight.text.trim(),
            matchCase: false,
            wholeWords: false,
          };
          highlight(newKeyword);
        }

        // Update the last processed highlight
        lastProcessedHighlightRef.current = activeHighlight;
      }
    } catch (error) {
      console.error('Error during navigation or highlighting:', error);
    }
  };

  // Single effect to handle highlight changes
  useEffect(() => {
    if (activeHighlight && isDocumentLoaded) {
      handleHighlightAndNavigation();
    }
  }, [activeHighlight, isDocumentLoaded]);

  // Remove the duplicate effects and logging
  useEffect(() => {
    let isMounted = true;

    const fetchDocument = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // If the documentUrl is already an S3 URL, use it directly
        if (documentUrl?.startsWith('https://') && documentUrl?.includes('your-objectstorage.com')) {
          setFileUrl(documentUrl);
          setIsLoading(false);
          return;
        }

        // Fetch the presigned URL from the stream endpoint
        const response = await fetch(documentUrl, {
          headers: {
            'Authorization': `Bearer ${user?.access_token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Parse the JSON response to get the presigned URL
        const data = await response.json();
        if (!data.url) {
          throw new Error('No presigned URL in response');
        }

        // Fetch the actual file using the presigned URL
        const fileResponse = await fetch(data.url);
        if (!fileResponse.ok) {
          throw new Error(`Failed to fetch file: ${fileResponse.status}`);
        }

        const blob = await fileResponse.blob();
        const url = URL.createObjectURL(blob);
        
        if (isMounted) {
          setFileUrl(url);
          setIsLoading(false);
        }

      } catch (err) {
        console.error('Error fetching document:', err);
        if (isMounted) {
          setError(err.message || 'Failed to load document');
          setIsLoading(false);
        }
      }
    };

    if (documentUrl) {
      fetchDocument();
    }

    return () => {
      isMounted = false;
      if (fileUrl && !fileUrl.startsWith('https://')) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [documentUrl, user]);

  const renderPdfViewer = () => (
    <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
      <div className="flex flex-col h-full bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl relative">
        <div className="flex-1 overflow-auto bg-transparent p-6">
          <Viewer
            fileUrl={fileUrl}
            plugins={[
              pageNavigationPluginInstance,
              toolbarPluginInstance,
              searchPluginInstance,
              zoomPluginInstance,
            ]}
            defaultScale={SpecialZoomLevel.PageFit}
            onDocumentLoad={(e) => {
              console.log('Document loaded event fired');
              setIsLoading(false);
              setIsDocumentLoaded(true);
              
              if (activeHighlight) {
                handleHighlightAndNavigation();
              }
            }}
            theme={{
              theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light',
            }}
          />
        </div>
        
        <div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-white dark:bg-gray-800 rounded-lg shadow-lg"
          style={{
            zIndex: 1,
          }}
        >
          <div className="flex items-center space-x-2 p-2">
            <div className="px-2">
              {zoomPluginInstance.ZoomOut && (
                <zoomPluginInstance.ZoomOut>
                  {(props) => (
                    <button 
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                      onClick={props.onClick}
                      disabled={props.isDisabled}
                    >
                      <ZoomOutIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    </button>
                  )}
                </zoomPluginInstance.ZoomOut>
              )}
            </div>
            <div className="px-2">
              {zoomPluginInstance.ZoomIn && (
                <zoomPluginInstance.ZoomIn>
                  {(props) => (
                    <button 
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                      onClick={props.onClick}
                      disabled={props.isDisabled}
                    >
                      <ZoomInIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    </button>
                  )}
                </zoomPluginInstance.ZoomIn>
              )}
            </div>
            <div className="h-4 w-px bg-gray-300 dark:bg-gray-600 mx-2" />
            <div className="px-2">
              {pageNavigationPluginInstance.GoToPreviousPage && (
                <pageNavigationPluginInstance.GoToPreviousPage>
                  {(props) => (
                    <button 
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                      onClick={props.onClick}
                      disabled={props.isDisabled}
                    >
                      <ChevronLeft className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    </button>
                  )}
                </pageNavigationPluginInstance.GoToPreviousPage>
              )}
            </div>
            <div className="px-2 flex items-center space-x-1 text-gray-700 dark:text-gray-300">
              {pageNavigationPluginInstance.CurrentPageInput && (
                <pageNavigationPluginInstance.CurrentPageInput />
              )}
              <span>/</span> 
              {pageNavigationPluginInstance.NumberOfPages && (
                <pageNavigationPluginInstance.NumberOfPages />
              )}
            </div>
            <div className="px-2">
              {pageNavigationPluginInstance.GoToNextPage && (
                <pageNavigationPluginInstance.GoToNextPage>
                  {(props) => (
                    <button 
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                      onClick={props.onClick}
                      disabled={props.isDisabled}
                    >
                      <ChevronRight className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    </button>
                  )}
                </pageNavigationPluginInstance.GoToNextPage>
              )}
            </div>
          </div>
        </div>
      </div>
    </Worker>
  );

  const renderDocument = ({ documentUrl, documentType, documentId, highlights, activeHighlight }) => {
    switch (true) {
      case documentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'):
      case documentType.includes('application/vnd.ms-excel'):
      case documentType.includes('text/csv'):
        return (
          <SmartSpreadsheetViewer
            documentUrl={documentUrl}
            documentId={documentId}
            user={user}
            onAskAboutData={onAskAboutData}
          />
        );

      case documentType.includes('application/pdf'):
        return renderPdfViewer();

      // ... rest of the cases ...
    }
  };

  const getCurrentDocument = () => {
    if (documents?.length > 0) {
      const currentDoc = documents[currentDocIndex];
      return {
        documentUrl: `${API_URL}/documents/stream/${currentDoc.id}`,
        documentType: currentDoc.file_type,
        documentId: currentDoc.id,
        highlights: highlights?.filter(h => h.documentId === currentDoc.id),
        activeHighlight: activeHighlight?.documentId === currentDoc.id ? activeHighlight : null
      };
    }
    return {
      documentUrl,
      documentType,
      documentId,
      highlights,
      activeHighlight
    };
  };

  const DocumentSwitcher = () => {
    if (!documents || documents.length <= 1) return null;

    return (
      <div className="sticky top-0 z-20 p-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              const newIndex = currentDocIndex > 0 ? currentDocIndex - 1 : documents.length - 1;
              setCurrentDocIndex(newIndex);
              onDocumentSwitch(documents[newIndex]);
            }}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            disabled={documents.length <= 1}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <div className="flex items-center space-x-2">
            <div className="text-sm font-medium text-gray-900 dark:text-white">
              {documents[currentDocIndex]?.filename}
            </div>
            <div className="text-sm text-gray-500">
              {currentDocIndex + 1} / {documents.length}
            </div>
          </div>

          <button
            onClick={() => {
              const newIndex = currentDocIndex < documents.length - 1 ? currentDocIndex + 1 : 0;
              setCurrentDocIndex(newIndex);
              onDocumentSwitch(documents[newIndex]);
            }}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            disabled={documents.length <= 1}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="h-full">
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <Loader className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center h-full text-red-500">
          <AlertCircle className="w-12 h-12 mb-4" />
          <p>{error}</p>
        </div>
      ) : (
        <div className="h-full flex flex-col">
          <DocumentSwitcher />
          <div className="flex-1 overflow-auto">
            {renderDocument(getCurrentDocument())}
          </div>
        </div>
      )}
    </div>
  );
};

export default UniversalDocumentViewer;